// React imports
import React, { useEffect, useState, useCallback } from "react";

// External library imports
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Container,
  Divider,
  Paper,
  Typography,
  TextField,
  Alert,
  Stack,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import FeedbackIcon from "@mui/icons-material/Feedback";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import SaveIcon from "@mui/icons-material/Save";
import { useDispatch } from "react-redux";
import { ThunkDispatch } from "@reduxjs/toolkit";

// Local imports
import { RootState } from "../../../store/Store";
import {
  fetchData,
  postData,
  FetchDataSuccessAction,
  FetchDataFailureAction,
} from "../../../store/actions";
import { useCustomToast } from "../../../components/Methods/SnackBar";
import { employeeKRAsUrl, submit360FeedbackUrl, check360AvailabilityUrl, get360FeedbackUrl } from "../../../Service/Apis";
import {
  getStatusChip,
  formatDate,
  KRAStatus,
  getPeriodShortLabel,
  getPeriodDaysWarningColor,
} from "../utils";
import RatingSlider from "../../../components/RatingSlider/RatingSlider";
import { EmployeeKRA, ManagerFeedbackData, AvailabilityInfo } from "./Types/Feedback360Types";

// Rating categories configuration
const RATING_CATEGORIES = [
  {
    key: 'communication_rating' as const,
    title: 'Communication & Transparency',
    description: 'How well does your manager communicate goals, expectations, and feedback?'
  },
  {
    key: 'support_rating' as const,
    title: 'Support & Guidance',
    description: 'Does your manager provide adequate support when you face challenges?'
  },
  {
    key: 'recognition_rating' as const,
    title: 'Recognition & Appreciation',
    description: 'How well does your manager recognize and appreciate your contributions?'
  },
  {
    key: 'development_rating' as const,
    title: 'Career Development & Growth',
    description: 'Does your manager actively support your career growth and skill development?'
  },
  {
    key: 'leadership_rating' as const,
    title: 'Leadership & Decision Making',
    description: 'How effectively does your manager lead the team and make decisions?'
  }
] as const;

const Feedback360: React.FC = () => {
  // useState hooks
  const [myKRAs, setMyKRAs] = useState<EmployeeKRA[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [feedbackDialogOpen, setFeedbackDialogOpen] = useState<boolean>(false);
  const [selectedKRA, setSelectedKRA] = useState<EmployeeKRA | null>(null);
  const [submittingFeedback, setSubmittingFeedback] = useState<boolean>(false);
  const [loadingFeedback, setLoadingFeedback] = useState<{ [key: string]: boolean }>({}); // Individual loading states
  const [feedbackSubmitted, setFeedbackSubmitted] = useState<{ [key: string]: boolean }>({});
  const [availabilityInfo, setAvailabilityInfo] = useState<AvailabilityInfo | null>(null);
  
  const [managerFeedback, setManagerFeedback] = useState<ManagerFeedbackData>({
    communication_rating: 0,
    support_rating: 0,
    recognition_rating: 0,
    development_rating: 0,
    leadership_rating: 0,
    comments: "",
  });

  // Other hooks
  const dispatch: ThunkDispatch<RootState, null, FetchDataSuccessAction | FetchDataFailureAction> = useDispatch();
  const showToast = useCustomToast();

  // Utility Functions
  const handleError = useCallback((error: unknown, userMessage: string, shouldToast: boolean = true): void => {
    if (shouldToast) {
      showToast(userMessage, "error", true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // showToast is stable, no need to re-create handleError

  // Functions
  const fetchMyKRAs = useCallback(async (): Promise<void> => {
    setLoading(true);
    try {
      // Fetch KRAs with 360° enabled
      const response = await dispatch(fetchData(employeeKRAsUrl));

      if (response && Array.isArray(response)) {
        // Filter KRAs where template has 360° enabled
        const eligibleKRAs = response.filter(
          (kra: EmployeeKRA & { has_360_enabled?: boolean }) => kra.has_360_enabled === true
        );
        setMyKRAs(eligibleKRAs);
        
        // Check 360° availability for current calendar year
        try {
          const availabilityResponse = await dispatch(fetchData(check360AvailabilityUrl));
          if (availabilityResponse) {
            setAvailabilityInfo(availabilityResponse as AvailabilityInfo);
            // Mark all KRAs as submitted if feedback already submitted for this year
            if (!availabilityResponse.available) {
              const feedbackStatusMap: { [key: string]: boolean } = {};
              eligibleKRAs.forEach((kra: EmployeeKRA) => {
                feedbackStatusMap[kra.employee_kra_id] = true;
              });
              setFeedbackSubmitted(feedbackStatusMap);
            }
          }
        } catch (error: unknown) {
          // Silently fail - availability check is not critical
        }
      } else {
        setMyKRAs([]);
      }
    } catch (error) {
      handleError(error, "Failed to load feedback opportunities");
      setMyKRAs([]);
    } finally {
      setLoading(false);
    }
  }, [dispatch, handleError]);

  const getDraftKey = (): string | null => {
    // Use manager email and calendar year for draft key
    if (availabilityInfo?.manager?.email && availabilityInfo?.calendar_year) {
      return `360_feedback_draft_${availabilityInfo.manager.email}_${availabilityInfo.calendar_year}`;
    }
    return null;
  };

  const loadDraftFromLocalStorage = (): boolean => {
    try {
      const draftKey = getDraftKey();
      if (!draftKey) return false;
      
      const savedDraft = localStorage.getItem(draftKey);
      
      if (savedDraft) {
        const draft = JSON.parse(savedDraft);
        setManagerFeedback(draft);
        showToast("Draft loaded from previous session", "info", true);
        return true;
      }
    } catch (error) {
      // Silently fail - draft loading is not critical
    }
    return false;
  };

  const saveDraftToLocalStorage = (): void => {
    try {
      const draftKey = getDraftKey();
      if (!draftKey) {
        showToast("Cannot save draft - manager info not available", "error", true);
        return;
      }
      
      localStorage.setItem(draftKey, JSON.stringify(managerFeedback));
      showToast("Draft saved successfully! You can continue later.", "success", true);
    } catch (error) {
      showToast("Failed to save draft", "error", true);
    }
  };

  const clearDraftFromLocalStorage = (): void => {
    try {
      const draftKey = getDraftKey();
      if (!draftKey) return;
      
      localStorage.removeItem(draftKey);
    } catch (error) {
      // Silently fail
    }
  };

  const loadExistingFeedback = useCallback(async (kraId: string): Promise<void> => {
    try {
      const response = await dispatch(fetchData(get360FeedbackUrl(kraId)));
      if (response && response.has_feedback && response.data) {
        setManagerFeedback({
          communication_rating: response.data.communication_rating || 0,
          support_rating: response.data.support_rating || 0,
          recognition_rating: response.data.recognition_rating || 0,
          development_rating: response.data.development_rating || 0,
          leadership_rating: response.data.leadership_rating || 0,
          comments: response.data.comments || "",
        });
      }
    } catch (error) {
      handleError(error, "Failed to load existing feedback");
    }
  }, [dispatch, handleError]);

  const handleOpenFeedbackDialog = async (kra: EmployeeKRA): Promise<void> => {
    // Set loading state for this specific KRA
    setLoadingFeedback(prev => ({ ...prev, [kra.employee_kra_id]: true }));
    
    setSelectedKRA(kra);
    
    try {
      // If feedback already submitted, load it for viewing
      if (feedbackSubmitted[kra.employee_kra_id]) {
        await loadExistingFeedback(kra.employee_kra_id);
      } else {
        // Try to load draft from localStorage first
        const draftLoaded = loadDraftFromLocalStorage();
        
        // If no draft found, reset to default form
        if (!draftLoaded) {
          setManagerFeedback({
            communication_rating: 0,
            support_rating: 0,
            recognition_rating: 0,
            development_rating: 0,
            leadership_rating: 0,
            comments: "",
          });
        }
      }
      
      setFeedbackDialogOpen(true);
    } finally {
      // Clear loading state for this KRA
      setLoadingFeedback(prev => ({ ...prev, [kra.employee_kra_id]: false }));
    }
  };
  
  const isViewMode = !availabilityInfo?.available;

  const handleSubmitFeedback = async (): Promise<void> => {
    // Validation
    const overallRating = calculateOverallRating();
    if (overallRating === 0) {
      showToast("Please provide at least one rating for your manager", "error", true);
      return;
    }

    if (!availabilityInfo?.available) {
      showToast("360° feedback is not available at this time", "error", true);
      return;
    }

    setSubmittingFeedback(true);
    
    try {
      // Submit feedback via API (new calendar year based endpoint)
      const payload: {
        communication_rating: number;
        support_rating: number;
        recognition_rating: number;
        development_rating: number;
        leadership_rating: number;
        comments: string;
        employee_kra_reference_id?: string;
      } = {
        communication_rating: managerFeedback.communication_rating,
        support_rating: managerFeedback.support_rating,
        recognition_rating: managerFeedback.recognition_rating,
        development_rating: managerFeedback.development_rating,
        leadership_rating: managerFeedback.leadership_rating,
        comments: managerFeedback.comments,
      };
      
      // Optionally include KRA reference if available
      if (selectedKRA) {
        payload.employee_kra_reference_id = selectedKRA.employee_kra_id;
      }
      
      await dispatch(postData(submit360FeedbackUrl, payload));
      
      showToast(`360° feedback submitted successfully for ${availabilityInfo.calendar_year}!`, "success", true);
      
      // Clear draft from localStorage after successful submission
      clearDraftFromLocalStorage();
      
      // Mark all KRAs as submitted (since it's per calendar year, not per KRA)
      const feedbackStatusMap: { [key: string]: boolean } = {};
      myKRAs.forEach((kra: EmployeeKRA) => {
        feedbackStatusMap[kra.employee_kra_id] = true;
      });
      setFeedbackSubmitted(feedbackStatusMap);
      
      // Update availability info
      setAvailabilityInfo({
        ...availabilityInfo,
        available: false,
        reason: `Already submitted for ${availabilityInfo.calendar_year}`,
      });
      
      // Close dialog
      setFeedbackDialogOpen(false);
      setSelectedKRA(null);
      
    } catch (error: unknown) {
      const axiosError = error as { payload?: { error?: string } } | Error;
      const errorMessage = ('payload' in axiosError && axiosError.payload?.error) || 
        (axiosError instanceof Error ? axiosError.message : undefined) || 
        "Failed to submit feedback";
      handleError(error, errorMessage);
    } finally {
      setSubmittingFeedback(false);
    }
  };

  const calculateOverallRating = (): number => {
    const ratings = [
      managerFeedback.communication_rating,
      managerFeedback.support_rating,
      managerFeedback.recognition_rating,
      managerFeedback.development_rating,
      managerFeedback.leadership_rating,
    ];
    
    const validRatings = ratings.filter(r => r > 0);
    if (validRatings.length === 0) return 0;
    
    // Calculate average of all 5 categories (only counting non-zero ratings)
    return validRatings.reduce((sum, r) => sum + r, 0) / validRatings.length;
  };

  // useEffect hooks
  useEffect(() => {
    fetchMyKRAs();
  }, [fetchMyKRAs]);

  if (loading) {
    return (
      <Box
        sx={{
          width: "100%",
          height: "80vh",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <CircularProgress size={60} />
      </Box>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 10, mb: 5 }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Typography
          variant="h4"
          sx={{ fontWeight: "bold", color: "primary.main", mb: 1 }}
        >
          360° Manager Feedback
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Provide confidential feedback about your manager to help improve team dynamics and organizational culture
        </Typography>
      </Box>

      {/* Availability Info */}
      {availabilityInfo && (
        <Alert 
          severity={availabilityInfo.available ? "info" : "warning"} 
          sx={{ mb: 3 }}
        >
          {availabilityInfo.available ? (
            <>
              <Typography variant="body1" sx={{ fontWeight: "600", mb: 0.5 }}>
                360° Feedback Available for {availabilityInfo.calendar_year}
              </Typography>
              <Typography variant="body2">
                Calendar Year: {availabilityInfo.year_period?.start} to {availabilityInfo.year_period?.end}
                {availabilityInfo.manager && ` • Manager: ${availabilityInfo.manager.name}`}
              </Typography>
            </>
          ) : (
            <>
              <Typography variant="body1" sx={{ fontWeight: "600", mb: 0.5 }}>
                {availabilityInfo.reason}
              </Typography>
              {availabilityInfo.submitted_date && (
                <Typography variant="body2">
                  Submitted on: {formatDate(availabilityInfo.submitted_date)}
                </Typography>
              )}
              {availabilityInfo.next_available_date && (
                <Typography variant="body2">
                  Next available: {formatDate(availabilityInfo.next_available_date)}
                </Typography>
              )}
            </>
          )}
        </Alert>
      )}

      {/* Empty State or KRA Cards */}
      {myKRAs.length === 0 ? (
        <Paper elevation={2} sx={{ p: 6, textAlign: "center" }}>
          <FeedbackIcon
            sx={{ fontSize: 80, color: "text.secondary", mb: 2 }}
          />
          <Typography variant="h6" sx={{ mb: 1, fontWeight: "600" }}>
            No Feedback Opportunities Available
          </Typography>
          <Typography variant="body2" color="text.secondary">
            You don't have any KRA assignments with 360° feedback enabled. Contact HR if you believe this is an error.
          </Typography>
        </Paper>
      ) : (
        /* KRA Cards */
        <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
          {myKRAs.map((kra) => (
            <Card key={kra.employee_kra_id} elevation={3}>
              <CardContent sx={{ p: 3 }}>
                {/* KRA Header */}
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "flex-start",
                    mb: 2,
                  }}
                >
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="h6" sx={{ fontWeight: "600", mb: 1 }}>
                      {kra.form_name}
                    </Typography>
                    <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", mb: 1 }}>
                      <Typography variant="body2" color="text.secondary">
                        <strong>Designation:</strong> {kra.designation}
                      </Typography>
                      {kra.manager_reviewed_by_name && (
                        <Typography variant="body2" color="text.secondary">
                          <strong>Manager:</strong> {kra.manager_reviewed_by_name}
                        </Typography>
                      )}
                    </Box>
                    <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", alignItems: "center" }}>
                      {availabilityInfo && availabilityInfo.year_period && (
                        <>
                          <Typography variant="body2" color="text.secondary">
                            <strong>360° Feedback Year:</strong> {availabilityInfo.calendar_year}
                          </Typography>
                          {!availabilityInfo.available && (
                            <Chip 
                              label={`Submitted for ${availabilityInfo.calendar_year}`}
                              size="small"
                              color="success"
                            />
                          )}
                        </>
                      )}
                    </Box>
                  </Box>
                  <Box sx={{ display: "flex", flexDirection: "column", gap: 1, alignItems: "flex-end" }}>
                    {feedbackSubmitted[kra.employee_kra_id] && (
                      <Chip
                        icon={<CheckCircleIcon />}
                        label="Feedback Submitted"
                        size="small"
                        color="success"
                      />
                    )}
                  </Box>
                </Box>

                <Divider sx={{ my: 2 }} />

                {/* Feedback Info */}
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      {kra.self_review_submitted_date 
                        ? `Self-review submitted on ${formatDate(kra.self_review_submitted_date)}`
                        : "Self-review pending"}
                    </Typography>
                  </Box>
                  <Button
                    variant={!availabilityInfo?.available ? "outlined" : "contained"}
                    size="small"
                    onClick={() => handleOpenFeedbackDialog(kra)}
                    disabled={loadingFeedback[kra.employee_kra_id] || !availabilityInfo?.available}
                    startIcon={loadingFeedback[kra.employee_kra_id] ? <CircularProgress size={16} /> : null}
                    aria-label={
                      !availabilityInfo?.available
                        ? `360° feedback already submitted for ${availabilityInfo?.calendar_year}`
                        : `Provide 360° feedback for ${kra.form_name}`
                    }
                    aria-busy={loadingFeedback[kra.employee_kra_id]}
                  >
                    {loadingFeedback[kra.employee_kra_id] 
                      ? "Loading..." 
                      : !availabilityInfo?.available
                        ? "Already Submitted" 
                        : "Submit Feedback"}
                  </Button>
                </Box>
              </CardContent>
            </Card>
          ))}
        </Box>
      )}

      {/* Feedback Dialog */}
      <Dialog
        open={feedbackDialogOpen}
        onClose={() => !submittingFeedback && setFeedbackDialogOpen(false)}
        maxWidth="lg"
        fullWidth
        aria-labelledby="feedback-dialog-title"
        aria-describedby="feedback-dialog-description"
      >
        <DialogTitle id="feedback-dialog-title">
          <Box>
            <Typography variant="h6" sx={{ fontWeight: "bold" }}>
              {!availabilityInfo?.available ? "View 360° Feedback" : "Provide 360° Feedback"}
            </Typography>
            <Typography variant="body2" color="text.secondary" id="feedback-dialog-description">
              {selectedKRA?.form_name} • {availabilityInfo?.manager?.name || selectedKRA?.manager_reviewed_by_name || "Your Manager"}
              {availabilityInfo?.calendar_year && ` • Year: ${availabilityInfo.calendar_year}`}
            </Typography>
            {!availabilityInfo?.available && (
              <Chip 
                label={`Already Submitted for ${availabilityInfo?.calendar_year}`}
                size="small" 
                color="success" 
                sx={{ mt: 1 }}
              />
            )}
          </Box>
        </DialogTitle>
        
        <DialogContent dividers>
          {!availabilityInfo?.available && (
            <Alert severity="info" sx={{ mb: 3 }}>
              You have already submitted 360° feedback for calendar year {availabilityInfo?.calendar_year}. 
              {availabilityInfo?.next_available_date && ` Next available: ${formatDate(availabilityInfo.next_available_date)}`}
            </Alert>
          )}

          <Stack spacing={3}>
            {/* Rating Sliders - Using Reusable Component */}
            {RATING_CATEGORIES.map((category) => (
              <RatingSlider
                key={category.key}
                title={category.title}
                description={category.description}
                value={managerFeedback[category.key]}
                onChange={(value) => setManagerFeedback({
                  ...managerFeedback,
                  [category.key]: value
                })}
                disabled={!availabilityInfo?.available}
              />
            ))}

            <Divider />

            {/* Overall Comments */}
            <TextField
              label="Overall Comments (Optional)"
              multiline
              rows={4}
              fullWidth
              value={managerFeedback.comments}
              onChange={(e) => setManagerFeedback({
                ...managerFeedback,
                comments: e.target.value
              })}
              placeholder="Share your thoughts about your manager's strengths, areas for improvement, or any other feedback..."
              helperText={`${managerFeedback.comments.length}/2000 characters - Your feedback helps improve team dynamics and organizational culture`}
              disabled={!availabilityInfo?.available}
              inputProps={{ 
                maxLength: 2000,
                'aria-label': 'Overall feedback comments',
                'aria-describedby': 'comments-helper-text'
              }}
              id="comments-helper-text"
            />

            <Divider />

            {/* Overall Rating - Auto-Calculated */}
            <Box sx={{ bgcolor: "#f5f5f5", p: 2, borderRadius: 2 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: "600", mb: 1 }}>
                Overall Manager Rating (Auto-Calculated)
              </Typography>
              <Box>
                <Typography variant="h4" sx={{ fontWeight: "700", color: "info.main", mb: 0.5 }}>
                  {calculateOverallRating().toFixed(2)}/5.00
                </Typography>
                <Typography variant="h6" sx={{ color: "info.main", fontWeight: "600" }}>
                  {((calculateOverallRating() / 5) * 100).toFixed(1)}%
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1 }}>
                  Average of all category ratings
                </Typography>
              </Box>
            </Box>
          </Stack>
        </DialogContent>

        <DialogActions sx={{ p: 2 }}>
          {!availabilityInfo?.available ? (
            <Button onClick={() => setFeedbackDialogOpen(false)}>
              Close
            </Button>
          ) : (
            <>
              <Button 
                onClick={() => setFeedbackDialogOpen(false)}
                disabled={submittingFeedback}
              >
                Cancel
              </Button>
              <Button
                variant="outlined"
                onClick={saveDraftToLocalStorage}
                disabled={submittingFeedback || !availabilityInfo?.available}
                startIcon={<SaveIcon />}
                aria-label="Save 360 feedback draft to continue later"
              >
                Save Draft
              </Button>
              <Button
                variant="contained"
                onClick={handleSubmitFeedback}
                disabled={submittingFeedback || calculateOverallRating() === 0 || !availabilityInfo?.available}
                startIcon={submittingFeedback ? <CircularProgress size={20} /> : null}
                aria-busy={submittingFeedback}
                aria-label="Submit 360 degree feedback for your manager"
                aria-disabled={submittingFeedback || calculateOverallRating() === 0 || !availabilityInfo?.available}
              >
                {submittingFeedback ? "Submitting..." : "Submit Feedback"}
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default Feedback360;
